const S = 16;
const CAT = { furniture: "#A8957D", fixture: "#60A5FA", appliance: "#FFB800" };
const PIPE = { drain: ["#10B981", 3], cold: ["#38BDF8", 1.5], hot: ["#F87171", 1.5] };

const Detail = ({ it, x, y, w, h, c }) => {
  const m = Math.min(w, h);
  switch (it.type) {
    case "toilet": return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w * 0.3} ry={h * 0.3} fill="none" stroke={c} />;
    case "kitchen_sink":
    case "vanity": return <circle cx={x + w / 2} cy={y + h / 2} r={m * 0.28} fill="none" stroke={c} />;
    case "tub": return <rect x={x + w * 0.08} y={y + h * 0.1} width={w * 0.84} height={h * 0.8} rx={m * 0.3} fill="none" stroke={c} />;
    case "shower": return <path d={`M${x} ${y} L${x + w} ${y + h} M${x + w} ${y} L${x} ${y + h}`} stroke={c} strokeOpacity={0.5} />;
    case "range": return [0.3, 0.7].flatMap((fx) => [0.3, 0.7].map((fy) => <circle key={`${fx}${fy}`} cx={x + w * fx} cy={y + h * fy} r={m * 0.13} fill="none" stroke={c} />));
    case "bed_king":
    case "bed_queen":
    case "bed_twin": {
      const v = it.face === "top" || it.face === "bottom";
      const py = it.face === "top" ? y + 4 : y + h - 4 - h * 0.14, px = it.face === "left" ? x + 4 : x + w - 4 - w * 0.14;
      return v ? <rect x={x + 4} y={py} width={w - 8} height={h * 0.14} rx={3} fill="none" stroke={c} />
        : <rect x={px} y={y + 4} width={w * 0.14} height={h - 8} rx={3} fill="none" stroke={c} />;
    }
    default: return null;
  }
};

export const FitoutItems = ({ items }) => (
  <g pointerEvents="none" data-testid="layer-furniture">
    {items.map((it, i) => {
      const [x, y, w, h] = [it.x * S, it.y * S, it.w * S, it.h * S];
      const c = CAT[it.cat];
      return (
        <g key={i}>
          <rect x={x} y={y} width={w} height={h} rx={it.type === "counter" ? 0 : 3} fill={c} fillOpacity={it.type === "counter" ? 0.06 : 0.1} stroke={c} strokeWidth={1} />
          <Detail it={it} x={x} y={y} w={w} h={h} c={c} />
          {it.label && Math.min(w, h) > 30 && it.type !== "bed_twin" && (
            <text x={x + w / 2} y={y + h / 2 + 3} textAnchor="middle" fontSize={8} fontFamily="JetBrains Mono" fill={c}>{it.label.toUpperCase()}</text>
          )}
        </g>
      );
    })}
  </g>
);

const Device = ({ e }) => {
  const x = e.x * S, y = e.y * S;
  if (e.type === "outlet" || e.type === "gfci") return (
    <g><circle cx={x} cy={y} r={5} fill="#0A0D14" stroke="#FACC15" strokeWidth={1.2} />
      <path d={`M${x - 1.8} ${y - 2.5}V${y + 2.5}M${x + 1.8} ${y - 2.5}V${y + 2.5}`} stroke="#FACC15" />
      {e.type === "gfci" && <text x={x + 7} y={y - 4} fontSize={6} fill="#FACC15" fontFamily="JetBrains Mono">GFI</text>}</g>
  );
  if (e.type === "switch") return <text x={x} y={y + 4} textAnchor="middle" fontSize={11} fontWeight="700" fill="#FACC15" fontFamily="Archivo">S</text>;
  if (e.type === "panel") return <rect x={x - 10} y={y - 4} width={20} height={8} fill="#FACC15" />;
  return (
    <g><circle cx={x} cy={y} r={8} fill="#0A0D14" stroke="#FACC15" strokeWidth={1.2} />
      <path d={`M${x - 5.5} ${y - 5.5}L${x + 5.5} ${y + 5.5}M${x + 5.5} ${y - 5.5}L${x - 5.5} ${y + 5.5}`} stroke="#FACC15" />
      {e.type !== "light" && <text x={x} y={y + 18} textAnchor="middle" fontSize={7} fill="#FACC15" fontFamily="JetBrains Mono">{e.type === "fan" ? "FAN" : "EXH"}</text>}</g>
  );
};

export const ElectricalLayer = ({ items }) => (
  <g pointerEvents="none" data-testid="layer-electrical">
    {items.filter((e) => e.type === "circuit").map((e, i) => {
      const [[x1, y1], [x2, y2]] = e.pts.map(([a, b]) => [a * S, b * S]);
      const mx = (x1 + x2) / 2 + (y2 - y1) * 0.2, my = (y1 + y2) / 2 - (x2 - x1) * 0.2;
      return <path key={`c${i}`} d={`M${x1} ${y1} Q${mx} ${my} ${x2} ${y2}`} fill="none" stroke="#FACC15" strokeOpacity={0.5} strokeDasharray="4 3" />;
    })}
    {items.filter((e) => e.type !== "circuit").map((e, i) => <Device key={i} e={e} />)}
  </g>
);

export const PlumbingLayer = ({ items }) => (
  <g pointerEvents="none" data-testid="layer-plumbing">
    {items.filter((p) => p.type !== "stack").map((p, i) => (
      <polyline key={i} points={p.pts.map(([a, b]) => `${a * S},${b * S}`).join(" ")} fill="none" stroke={PIPE[p.type][0]} strokeWidth={PIPE[p.type][1]} strokeOpacity={0.85} strokeLinejoin="round" />
    ))}
    {items.filter((p) => p.type === "stack").map((p, i) => (
      <g key={`s${i}`}><circle cx={p.x * S} cy={p.y * S} r={6} fill="#10B981" />
        <text x={p.x * S + 9} y={p.y * S - 6} fontSize={8} fill="#10B981" fontFamily="JetBrains Mono">STACK</text></g>
    ))}
  </g>
);

export const LAYERS = [
  ["furniture", "Furniture & fixtures", "#A8957D"],
  ["electrical", "Electrical", "#FACC15"],
  ["plumbing", "Plumbing", "#38BDF8"],
];

export const LayerToggles = ({ layers, setLayers }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="layer-toggles">
    {LAYERS.map(([k, label, col]) => (
      <button key={k} data-testid={`layer-toggle-${k}`} onClick={() => setLayers((l) => ({ ...l, [k]: !l[k] }))}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-colors ${layers[k] ? "border-[var(--line)] text-white bg-white/[0.04]" : "border-[var(--line)]/50 text-[var(--muted)]/60"}`}>
        <span className="w-2 h-2" style={{ background: layers[k] ? col : "transparent", border: `1px solid ${col}` }} />{label}
      </button>
    ))}
  </div>
);
